import type { input } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Stack, TextField, Typography } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { CasinoCategoryRow } from '../../../@types/casino';
import API_ROUTES from '../../../constants/apiRoutes';
import { QUERY_KEYS } from '../../../constants';
import { useModal } from '../../../contexts/ModalContext';
import { useMarketplace } from '../../../hooks/useMarketplace';
import useMutateData from '../../../hooks/useMutateData';
import { buildCasinoCategoryUpdatePayload } from '../../../helpers/casino';
import { CasinoCategoryFormSchemaData, casinoCategoryFormSchema } from '../../../schema';
import FormModalLayout from '../../layouts/FormModalLayout';

const styles = {
  accordion: {
    boxShadow: 'none',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: '8px !important',
    '&:before': {
      display: 'none',
    },
    '&.Mui-expanded': {
      m: 0,
    },
  },
};

const getDefaultLocale = (languages: string[], item?: CasinoCategoryRow) =>
  languages.reduce(
    (acc, language) => ({
      ...acc,
      [language]: {
        name: item?.locale?.[language]?.name ?? '',
        providerCategoryName: item?.locale?.[language]?.providerCategoryName ?? '',
      },
    }),
    {} as NonNullable<CasinoCategoryFormSchemaData['locale']>
  );

const getLocalePayload = (locale: CasinoCategoryFormSchemaData['locale'], itemLocale?: CasinoCategoryRow['locale']) =>
  Object.entries(locale ?? {}).reduce<NonNullable<CasinoCategoryFormSchemaData['locale']>>(
    (payload, [language, value]) => {
      const hasValue = !!value?.name || !!value?.providerCategoryName;
      const existed = !!itemLocale?.[language];

      if (!hasValue && !existed) {
        return payload;
      }

      payload[language] = {
        name: value?.name,
        providerCategoryName: value?.providerCategoryName,
      };
      return payload;
    },
    {}
  );

const CasinoCategoryForm = ({ closeModal }: { closeModal?: () => void }) => {
  const { item } = useModal<CasinoCategoryRow>();
  const { config } = useMarketplace();

  const supportedLanguages = useMemo(
    () => config?.marketplaceConfig?.languageConfig?.supportedLanguages ?? [],
    [config]
  );

  const { createData, updateData } = useMutateData(
    item ? API_ROUTES.CASINO.CATEGORY_UPDATE : API_ROUTES.CASINO.CATEGORY_CREATE,
    [QUERY_KEYS.casinoCategories],
    'casino'
  );

  const defaultValues = useMemo<CasinoCategoryFormSchemaData>(
    () => ({
      name: item?.name ?? '',
      providerCategoryName: item?.providerCategoryName ?? '',
      locale: getDefaultLocale(supportedLanguages, item),
    }),
    [item, supportedLanguages]
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<input<typeof casinoCategoryFormSchema>, unknown, CasinoCategoryFormSchemaData>({
    defaultValues,
    resolver: zodResolver(casinoCategoryFormSchema),
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const handleClose = () => {
    reset(defaultValues);
    closeModal?.();
  };

  const onSubmit = (data: CasinoCategoryFormSchemaData) => {
    const locale = getLocalePayload(data.locale, item?.locale);

    const payload = {
      name: data.name ?? '',
      providerCategoryName: data.providerCategoryName,
      locale: Object.keys(locale).length ? locale : undefined,
    };

    if (item) {
      updateData(item.id, buildCasinoCategoryUpdatePayload(payload), handleClose, 'Category updated successfully.');
      return;
    }

    createData(payload, handleClose, 'Category created successfully.');
  };

  return (
    <FormModalLayout
      onSave={handleSubmit(onSubmit)}
      label={item ? 'Edit category' : 'Add new category'}
      onClose={handleClose}
      isEdit={!!item}
      showEditTabs={false}
      disableSubmit={!isDirty}
    >
      <TextField
        label='Category name'
        {...register('name')}
        error={!!errors.name}
        helperText={errors.name?.message}
        required
      />
      <TextField
        label='Provider category name'
        {...register('providerCategoryName')}
        error={!!errors.providerCategoryName}
        helperText={errors.providerCategoryName?.message}
      />
      <Accordion disableGutters defaultExpanded sx={styles.accordion}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant='subtitle1'>Category translations</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ display: 'grid', gap: 2 }}>
          {supportedLanguages.map((language) => (
            <Stack key={language} spacing={1.5}>
              <Typography variant='subtitle2'>{language}</Typography>
              <TextField
                label='Category name'
                {...register(`locale.${language}.name`)}
                error={!!errors.locale?.[language]?.name}
                helperText={errors.locale?.[language]?.name?.message}
              />
              <TextField
                label='Provider category name'
                {...register(`locale.${language}.providerCategoryName`)}
                error={!!errors.locale?.[language]?.providerCategoryName}
                helperText={errors.locale?.[language]?.providerCategoryName?.message}
              />
            </Stack>
          ))}
        </AccordionDetails>
      </Accordion>
    </FormModalLayout>
  );
};

export default CasinoCategoryForm;
